import { useCallback, useEffect, useState } from "react";
import { ExternalLink, CreditCard, Mail, BookOpen, Landmark, BarChart3, Globe, FileText, ShieldCheck, Link as LinkIcon, Pencil, Plus, Trash2, ArrowUp, ArrowDown, Save, Loader2 } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import FolderShortcutsCard from "./FolderShortcutsCard";

const ICONS = { link: LinkIcon, card: CreditCard, mail: Mail, book: BookOpen, bank: Landmark, chart: BarChart3, globe: Globe, file: FileText, shield: ShieldCheck };
const ICON_LABELS = { link: "Link", card: "Betaalkaart", mail: "E-mail", book: "Boekhouding", bank: "Bank", chart: "Statistiek", globe: "Website", file: "Document", shield: "Beveiliging" };
const EMPTY = { name: "", url: "https://", description: "", icon: "link" };

export function ShortcutCard({ link }) {
  const Icon = ICONS[link.icon] || LinkIcon;
  return (
    <a href={link.url} target="_blank" rel="noopener noreferrer" data-testid={`admin-shortcut-${link.id}`}
      className="block bg-card border-2 border-foreground heavy-shadow p-5 hover:bg-foreground hover:text-background transition-colors group">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div className="flex items-center gap-4 min-w-0">
          <div className="w-12 h-12 border-2 border-foreground bg-accent text-accent-foreground flex items-center justify-center shrink-0 group-hover:border-background">
            <Icon className="w-6 h-6" />
          </div>
          <div className="min-w-0">
            <div className="stat-label group-hover:text-background/80 truncate">{link.description || "Externe link"}</div>
            <div className="font-display font-black text-2xl uppercase tracking-tight truncate">{link.name}</div>
            <div className="text-xs text-muted-foreground group-hover:text-background/80 mt-0.5">Opent in een nieuw tabblad</div>
          </div>
        </div>
        <ExternalLink className="w-6 h-6 shrink-0" />
      </div>
    </a>
  );
}

export default function AdminShortcuts() {
  const [links, setLinks] = useState(null);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try { setLinks((await api.get("/admin/links")).data.links); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/admin/links", { links: editing.map(({ name, url, description, icon }) => ({ name, url, description, icon })) });
      setLinks(data.links); setEditing(null); toast.success("Snelkoppelingen opgeslagen.");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    setSaving(false);
  };
  const update = (i, patch) => setEditing((l) => l.map((x, j) => (j === i ? { ...x, ...patch } : x)));
  const move = (i, d) => setEditing((l) => { const n = [...l]; const j = i + d; if (j < 0 || j >= n.length) return l; [n[i], n[j]] = [n[j], n[i]]; return n; });
  const input = "w-full border-2 border-foreground bg-background px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary";

  return (
    <>
      {(links || []).map((l) => <ShortcutCard key={l.id} link={l} />)}
      <FolderShortcutsCard />
      <button onClick={() => setEditing((links || []).map((l) => ({ ...l })))} data-testid="admin-shortcuts-edit"
        className="border-2 border-dashed border-foreground/40 p-5 text-left hover:border-foreground hover:bg-muted/40 transition-colors flex items-center gap-3 text-muted-foreground hover:text-foreground">
        <Pencil className="w-5 h-5 shrink-0" />
        <div>
          <div className="font-display font-black uppercase tracking-tight">Snelkoppelingen bewerken</div>
          <div className="text-xs">Voeg links toe naar Resend, e-Boekhouden, je bank …</div>
        </div>
      </button>

      <Dialog open={!!editing} onOpenChange={(o) => { if (!o && !saving) setEditing(null); }}>
        <DialogContent className="max-w-2xl border-2 border-foreground rounded-none heavy-shadow max-h-[85vh] overflow-y-auto" data-testid="admin-shortcuts-dialog">
          <DialogHeader>
            <DialogTitle className="font-display font-black uppercase tracking-tight">Snelkoppelingen</DialogTitle>
            <DialogDescription>Kaarten bovenaan /beheer die een externe pagina in een nieuw tabblad openen. Maximaal 12.</DialogDescription>
          </DialogHeader>
          {editing && (
            <div className="space-y-3">
              {editing.length === 0 && <div className="text-sm text-muted-foreground">Nog geen snelkoppelingen.</div>}
              {editing.map((l, i) => (
                <div key={i} className="border-2 border-foreground p-3 grid gap-2 sm:grid-cols-[1fr_1fr_auto]" data-testid={`admin-shortcut-row-${i}`}>
                  <label className="block"><span className="stat-label">Naam</span>
                    <input value={l.name} maxLength={40} onChange={(e) => update(i, { name: e.target.value })} data-testid={`admin-shortcut-name-${i}`} className={input} placeholder="bijv. Resend" /></label>
                  <label className="block"><span className="stat-label">Omschrijving (optioneel)</span>
                    <input value={l.description} maxLength={80} onChange={(e) => update(i, { description: e.target.value })} data-testid={`admin-shortcut-desc-${i}`} className={input} placeholder="bijv. E-mailverzending" /></label>
                  <div className="flex items-end gap-1 justify-end">
                    <button type="button" onClick={() => move(i, -1)} disabled={i === 0} className="p-1.5 border-2 border-foreground disabled:opacity-30" aria-label="Omhoog"><ArrowUp className="w-4 h-4" /></button>
                    <button type="button" onClick={() => move(i, 1)} disabled={i === editing.length - 1} className="p-1.5 border-2 border-foreground disabled:opacity-30" aria-label="Omlaag"><ArrowDown className="w-4 h-4" /></button>
                    <button type="button" onClick={() => setEditing((x) => x.filter((_, j) => j !== i))} className="p-1.5 border-2 border-destructive text-destructive" aria-label="Verwijderen" data-testid={`admin-shortcut-remove-${i}`}><Trash2 className="w-4 h-4" /></button>
                  </div>
                  <label className="block sm:col-span-2"><span className="stat-label">URL</span>
                    <input value={l.url} maxLength={500} onChange={(e) => update(i, { url: e.target.value })} data-testid={`admin-shortcut-url-${i}`} className={`${input} font-mono-num`} placeholder="https://…" /></label>
                  <label className="block"><span className="stat-label">Icoon</span>
                    <select value={l.icon} onChange={(e) => update(i, { icon: e.target.value })} data-testid={`admin-shortcut-icon-${i}`} className={input}>
                      {Object.keys(ICONS).map((k) => <option key={k} value={k}>{ICON_LABELS[k]}</option>)}
                    </select></label>
                </div>
              ))}
              <div className="flex items-center justify-between gap-2 flex-wrap pt-1">
                <button type="button" onClick={() => setEditing((x) => [...x, { ...EMPTY }])} disabled={editing.length >= 12} data-testid="admin-shortcut-add"
                  className="btn-brutal bg-background text-foreground px-3 py-1.5 font-display uppercase font-bold text-xs inline-flex items-center gap-1.5 disabled:opacity-40"><Plus className="w-3.5 h-3.5" /> Snelkoppeling toevoegen</button>
                <div className="flex gap-2">
                  <button type="button" onClick={() => setEditing(null)} disabled={saving} className="btn-brutal bg-background text-foreground px-3 py-1.5 font-display uppercase font-bold text-xs">Annuleren</button>
                  <button type="button" onClick={save} disabled={saving || editing.some((l) => !l.name.trim() || l.url.trim().length < 8)} data-testid="admin-shortcuts-save"
                    className="btn-brutal bg-primary text-primary-foreground px-3 py-1.5 font-display uppercase font-bold text-xs inline-flex items-center gap-1.5 disabled:opacity-40">
                    {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />} Opslaan
                  </button>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
